// Pure arithmetic + view-model helpers for AssembliesManagerPage's cost & price
// card, components table and operations table. No React, no fetch — every
// figure the page prints is derived here so it can be unit-tested and so the
// same formula is used for the server-fed model (GET /assemblies/{id}/pricing)
// and the local fallback (stored rollups + calculate-assembly-ops) alike.
//
// Findings addressed (audit_condensed.md): F02 (three labelled per-set rows),
// F06 (stored vs live rollup, stale flag), F07 (coating counted twice when the
// assembly owns a Coating row), F20 (manufactured / purchased / operations
// buckets), F21 (primary qty from the ppq input), F22 (minutes × $/hr behind
// the ops dollar), F23 (below-cost consumable book price), F51 (only send
// assembly_price when it changed).
//
// Every helper tolerates null/undefined/NaN and never throws.

import { num, marginPct, approxEq, fmtRate, fmtPct } from './priceMath';

/** Quote-page default sell rates — the same set the quote page seeds a new
 * quote with (QuoteManagementPage quoteSetup defaults) and the defaults on
 * AssemblyOpsRequest / GET /assemblies/{id}/pricing in jobq-api. Fractions
 * for the markups; coating_markup null = "same as material". */
export const ASSEMBLY_DEFAULT_QUOTE_RATES = Object.freeze({
  laser_rate: 175,
  tap_rate: 130,
  brake_rate: 130,
  fabrication_rate: 130,
  blast_rate: 100,
  finish_rate: 100,
  material_markup: 0.20,
  coating_markup: null,
});

export const DEFAULT_RATE_SET_NAME = 'default quote rates';

/** Fallback labour COST rates ($/hr) for the unsaved-grid estimate. Mirrors
 * DEFAULT_SHOP_RATES in jobq-api/main.py — employee wage cost, not the selling
 * rate. Coating is 0 because it is outsourced: its cost is the dollar amount
 * typed on the row. */
export const ASSEMBLY_OP_COST_RATES = Object.freeze({
  Laser: 25.0, Tap: 25.0, Brake: 26.0, Blast: 27.0, Finish: 23.0, Coating: 0.0,
  Cutting: 27.0, Drilling: 27.0, Milling: 27.0, Lathe: 27.0,
  Welding: 27.0, Assembly: 27.0, Tapping: 27.0,
  default: 27.0,
});

/** Markup used when a consumable needs a suggested price (cost × (1 + m)). */
export const CONSUMABLE_SUGGESTED_MARKUP = 0.20;

/** "laser $175.00/hr · tap $130.00/hr · … · material markup 20.0% · coating markup = material" */
export function rateSetLabel(rates) {
  const r = { ...ASSEMBLY_DEFAULT_QUOTE_RATES, ...(rates || {}) };
  const parts = [
    `laser ${fmtRate(r.laser_rate)}`,
    `tap ${fmtRate(r.tap_rate)}`,
    `brake ${fmtRate(r.brake_rate)}`,
    `fab ${fmtRate(r.fabrication_rate)}`,
    `blast ${fmtRate(r.blast_rate)}`,
    `finish ${fmtRate(r.finish_rate)}`,
    `material markup ${fmtPct(r.material_markup)}`,
    r.coating_markup === null || r.coating_markup === undefined
      ? 'coating markup = material'
      : `coating markup ${fmtPct(r.coating_markup)}`,
  ];
  return parts.join(' · ');
}

/** Query string for GET /assemblies/{id}/pricing — explicit so the printed
 * rate set is exactly what was requested. coating_markup null is omitted
 * (server default = material markup). */
export function pricingQueryString(rates) {
  const r = { ...ASSEMBLY_DEFAULT_QUOTE_RATES, ...(rates || {}) };
  const qs = [];
  ['laser_rate', 'tap_rate', 'brake_rate', 'fabrication_rate', 'blast_rate', 'finish_rate', 'material_markup', 'coating_markup']
    .forEach((k) => {
      if (r[k] === null || r[k] === undefined || r[k] === '') return;
      qs.push(`${k}=${encodeURIComponent(r[k])}`);
    });
  return qs.join('&');
}

// ---------------------------------------------------------------------------
// Part rollup — mirror of the single server formula (spec §3 B1,
// update_all_part_costs in jobq-api): cost = labor + gas + cons + material +
// coating; price = labor + gas + cons + (material + coating) × (1 + markup%).
// markup NULL → 10.0, but an explicit 0 stays 0.
// ---------------------------------------------------------------------------
export function partRollup(c) {
  const src = c || {};
  const labor = num(src.cost_labor);
  const gas = num(src.estimated_gas_cost);
  const cons = num(src.estimated_consumable_cost);
  const material = num(src.material_cost_component);
  const coating = num(src.coating_cost_per_unit);
  const markupPercent = (src.markup_percent === null || src.markup_percent === undefined)
    ? 10.0 : num(src.markup_percent, 10.0);
  const cost = labor + gas + cons + material + coating;
  const price = labor + gas + cons + (material + coating) * (1 + markupPercent / 100);
  return {
    cost_labor_material: cost,
    price_labor_material: price,
    markup_percent: markupPercent,
    components: { labor, gas, consumables: cons, material, coating },
  };
}

/** The component fields the "Recalculate part costs" PATCH sends so the server
 * recomputes the rollup (spec B1: any of the six triggers it). Only the keys
 * the row actually carries are sent (older API rows lack some).
 * `markup_percent` is deliberately NOT sent: GET /parts coerces a stored 0 to
 * 10 (F15), so echoing it back would clobber an explicit 0% markup — and the
 * server merges the payload over the stored row, so five fields are enough. */
export const PART_ROLLUP_COMPONENT_FIELDS = Object.freeze([
  'cost_labor', 'estimated_gas_cost', 'estimated_consumable_cost',
  'material_cost_component', 'coating_cost_per_unit',
]);

export function rollupPatchPayload(comp) {
  const out = {};
  PART_ROLLUP_COMPONENT_FIELDS.forEach((k) => {
    if (comp && comp[k] !== undefined) out[k] = comp[k];
  });
  return out;
}

/** True when the row carries enough stored components to reproduce the
 * server rollup in the UI (gas + consumables are the tell: the pre-provenance
 * API returned only material, labor, total, price). */
export function hasRollupComponents(comp) {
  return !!comp && comp.estimated_gas_cost !== undefined && comp.estimated_consumable_cost !== undefined
    && comp.cost_labor !== undefined && comp.material_cost_component !== undefined;
}

/** F06: a stored total more than a cent away from its own components is
 * stale. A cent, not half a cent: stored totals are kept at 2 dp while the
 * components carry 4, so sub-cent residuals are rounding, not drift. */
export const ROLLUP_STALE_TOLERANCE = 0.01;

/**
 * Per-component cost provenance for the parts table and the card.
 *   stored  — parts.cost_labor_material as saved
 *   live    — server `live_total` when present, else the UI rollup over the
 *             stored components when the row carries them, else null
 *   stale   — |live − stored| > ROLLUP_STALE_TOLERANCE (server `stale` wins when present)
 */
export function componentCostView(comp) {
  const c = comp || {};
  const stored = c.stored_total !== undefined && c.stored_total !== null
    ? num(c.stored_total, null)
    : (c.cost_labor_material === null || c.cost_labor_material === undefined ? null : num(c.cost_labor_material, null));
  let live = null;
  let liveSource = 'n/a';
  if (c.live_total !== undefined && c.live_total !== null) {
    live = num(c.live_total, null);
    liveSource = 'server';
  } else if (hasRollupComponents(c)) {
    live = partRollup(c).cost_labor_material;
    liveSource = 'ui';
  }
  let stale;
  if (typeof c.stale === 'boolean') stale = c.stale;
  else stale = live !== null && stored !== null && !approxEq(live, stored, ROLLUP_STALE_TOLERANCE);
  const delta = (live !== null && stored !== null) ? live - stored : null;
  const bookPrice = c.book_price !== undefined ? c.book_price : c.price;
  return {
    stored,
    live,
    liveSource,
    stale,
    delta,
    material: c.material_cost_component === undefined ? null : num(c.material_cost_component, null),
    materialMethod: c.material_price_method || null,
    labor: c.cost_labor === undefined ? null : num(c.cost_labor, null),
    gas: c.estimated_gas_cost === undefined ? null : num(c.estimated_gas_cost),
    consumables: c.estimated_consumable_cost === undefined ? null : num(c.estimated_consumable_cost),
    coating: c.coating_cost_per_unit === undefined ? null : num(c.coating_cost_per_unit),
    costUpdatedAt: c.cost_updated_at || null,
    rollupFormula: c.rollup_formula || null,   // server-authored, when the row carries one
    bookPrice: (bookPrice === null || bookPrice === undefined) ? null : num(bookPrice, null),
    bookPriceSource: c.book_price_source || null,
    bookPriceQuoteId: c.book_price_quote_id ?? null,
    bookPriceSetAt: c.book_price_set_at || null,
  };
}

/** Formula string for one component's rollup, e.g.
 * "labor $0.91 + gas $0.38 + consumables $0.01 + material $39.92 + coating $15.00 = $56.21".
 * The server's own `rollup_formula` wins when the row carries one. */
export function componentRollupFormula(view) {
  if (!view || view.live === null) return null;
  if (view.rollupFormula) return String(view.rollupFormula);
  const t = (label, v) => `${label} $${num(v).toFixed(2)}`;
  return `${t('labor', view.labor)} + ${t('gas', view.gas)} + ${t('consumables', view.consumables)} + ${t('material', view.material)} + ${t('coating', view.coating)} = $${num(view.live).toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Purchased components (consumables)
// ---------------------------------------------------------------------------
/**
 * Below-cost = a book price that IS set (> 0) and sits below the vendor cost.
 * A null/0 book price is "not set", not below cost. Server `below_cost` wins
 * only when the book price is set (a server true on a $0 book would otherwise
 * contradict the "—" the row prints).
 */
export function consumableView(cons, materialMarkup = CONSUMABLE_SUGGESTED_MARKUP) {
  const c = cons || {};
  const cost = (c.cost === null || c.cost === undefined) ? null : num(c.cost, null);
  const book = (c.price === null || c.price === undefined) ? null : num(c.price, null);
  const hasBook = book !== null && book > 0;
  const uiBelow = hasBook && cost !== null && cost > 0 && book + 0.005 < cost;
  const belowCost = hasBook && typeof c.below_cost === 'boolean' ? c.below_cost : uiBelow;
  // The markup behind a server suggested_price when it says which; else ours.
  const markup = (c.suggested_price_markup !== null && c.suggested_price_markup !== undefined)
    ? num(c.suggested_price_markup, num(materialMarkup, CONSUMABLE_SUGGESTED_MARKUP))
    : num(materialMarkup, CONSUMABLE_SUGGESTED_MARKUP);
  const suggested = (c.suggested_price !== null && c.suggested_price !== undefined)
    ? num(c.suggested_price, null)
    : (cost === null ? null : cost * (1 + markup));
  const vendorName = c.cost_vendor_name || null;
  const vendorCount = (c.vendor_count === null || c.vendor_count === undefined) ? null : num(c.vendor_count, null);
  return {
    cost,
    book,
    hasBook,
    belowCost,
    suggested,
    suggestedMarkup: markup,
    suggestedFormula: cost === null ? null
      : `$${cost.toFixed(2)} × (1 + ${(markup * 100).toFixed(0)}%) = $${num(suggested).toFixed(2)}`,
    costSource: c.cost_source || 'preferred vendor, else highest vendor cost',
    costSourceIsServer: !!c.cost_source,
    costVendorId: c.cost_vendor_id ?? null,
    costVendorName: vendorName,
    vendorCount,
    // "Foley (preferred)" / "McMaster, highest of 2 vendors" — for the badge
    costVendorLabel: vendorName
      ? `${vendorName}${c.cost_source ? ` — ${c.cost_source}` : ''}`
      : (c.cost_source || (vendorCount !== null ? `${vendorCount} vendor${vendorCount === 1 ? '' : 's'}` : null)),
    bookSource: c.book_price_source || null,
    bookQuoteId: c.book_price_quote_id ?? null,
    bookSetAt: c.book_price_set_at || null,
  };
}

// ---------------------------------------------------------------------------
// Assembly operations
// ---------------------------------------------------------------------------
/** Unsaved-grid estimate at standard WAGE rates. Rows are the grid rows
 * ({step, minutes, coatCost}). Coating rows carry a typed dollar, not minutes. */
export function estimateOpsFromGrid(rows, rateTable = ASSEMBLY_OP_COST_RATES) {
  const out = [];
  let laborCost = 0;
  let coatingCost = 0;
  (rows || []).forEach((r) => {
    if (!r || !r.step) return;
    // Rows are emitted in the RAW calculator shape ({station, minutes, cost,
    // price, true_rate, true_rate_source, source}) so opsRowsView renders
    // saved and estimated rows through one code path.
    if (r.step === 'Coating') {
      const c = num(r.coatCost);
      coatingCost += c;
      out.push({ station: 'Coating', minutes: null, cost: c, price: null, source: 'estimate', is_outsourced: true });
      return;
    }
    const mins = num(r.minutes);
    if (mins <= 0) return;
    const rate = rateTable[r.step] ?? rateTable.default;
    const cost = (mins / 60) * rate;
    laborCost += cost;
    out.push({ station: r.step, minutes: mins, cost, price: null, true_rate: rate, true_rate_source: 'standard', source: 'estimate' });
  });
  return {
    rows: out,
    laborCost,
    coatingCost,
    total: laborCost + coatingCost,
    hasCoatingRow: (rows || []).some((r) => r && r.step === 'Coating' && num(r.coatCost) > 0),
  };
}

/** Human label for an ops row's cost-rate provenance. `source` is the engine's
 * labor_rates_with_provenance tag ('punch_weighted_90d' | 'default' |
 * 'fabrication_fallback', or 'standard' for the unsaved-grid estimate);
 * `basis` is the free-text the pricing endpoint attaches when it had to imply
 * the rate from cost ÷ hours on a pre-provenance row. */
export function rateSourceLabel(source, basis) {
  switch (source) {
    case 'punch_weighted_90d': return 'wages, 90-day punch-weighted';
    case 'default': return 'default wage table (no punches)';
    case 'fabrication_fallback': return 'Fabrication wage rate (no punches for this sub-process)';
    case 'standard': return 'standard wage table (unsaved estimate)';
    default:
      if (source && source !== 'n/a') return String(source);
      return basis ? `implied: cost ÷ hours (${basis})` : 'wages, 90-day';
  }
}

/**
 * Normalise calculate-assembly-ops / pricing.operations rows for the table.
 * Pre-provenance rows carry only {station, minutes, cost, price}; the $/hr is
 * then IMPLIED from cost ÷ hours (and labelled so). Post-provenance rows carry
 * true_rate / quoted_rate / source / true_rate_source / markup / history_n;
 * GET /assemblies/{id}/pricing rows may instead carry an implied true_rate
 * plus `true_rate_basis` text.
 */
export function opsRowsView(operations) {
  return (operations || []).map((op) => {
    const o = op || {};
    const minutes = (o.minutes === null || o.minutes === undefined) ? null : num(o.minutes, null);
    const hours = minutes === null ? (o.hours === null || o.hours === undefined ? null : num(o.hours, null)) : minutes / 60;
    const cost = num(o.cost);
    const price = (o.price === null || o.price === undefined) ? null : num(o.price, null);
    const isOutsourced = minutes === null || o.source === 'vendor';
    let costRate = null; let costRateSource = null;
    if (!isOutsourced) {
      if (o.true_rate !== null && o.true_rate !== undefined) {
        costRate = num(o.true_rate, null);
        costRateSource = rateSourceLabel(o.true_rate_source, o.true_rate_basis);
      } else if (hours && hours > 0) {
        costRate = cost / hours;
        costRateSource = 'implied: cost ÷ hours (wages, 90-day)';
      }
    }
    let priceRate = null; let priceRateSource = null;
    if (!isOutsourced) {
      if (o.quoted_rate !== null && o.quoted_rate !== undefined) {
        priceRate = num(o.quoted_rate, null);
        priceRateSource = DEFAULT_RATE_SET_NAME;
      } else if (hours && hours > 0 && price !== null) {
        priceRate = price / hours;
        priceRateSource = `implied: price ÷ hours (${DEFAULT_RATE_SET_NAME})`;
      }
    }
    let markup = (o.markup === null || o.markup === undefined) ? null : num(o.markup, null);
    if (markup === null && isOutsourced && cost > 0 && price !== null) markup = price / cost - 1;
    return {
      station: o.station || '?',
      minutes,
      hours,
      cost,
      price,
      isOutsourced,
      costRate,
      costRateSource,
      priceRate,
      priceRateSource,
      markup,
      source: o.source || (isOutsourced ? 'vendor' : 'assembly_row'),
      historyN: o.history_n ?? null,
      // True only when the ENGINE labelled the rates (A1 provenance rows).
      // A pricing-endpoint row that implied its $/hr from cost ÷ hours
      // (true_rate_basis, no true_rate_source) is not provenance.
      hasProvenance: (o.true_rate_source !== undefined && o.true_rate_source !== null
        && o.true_rate_source !== 'standard' && o.true_rate_source !== 'n/a')
        || o.source === 'assembly_row' || o.source === 'vendor',
    };
  });
}

/** Σ over outsourced rows (Coating etc.) — the dollar the caption breaks out. */
export function opsCoatingCost(operations) {
  return opsRowsView(operations).filter((r) => r.isOutsourced).reduce((s, r) => s + r.cost, 0);
}

export function opsHasCoatingRow(operations) {
  return (operations || []).some((o) => o && o.station === 'Coating' && num(o.cost) > 0);
}

/** F07: when the assembly prices Coating on its own row AND the primary part's
 * stored rollup already contains coating_cost_per_unit, the card would count
 * it twice. This is the amount to SUBTRACT from the live shop cost. */
export function coatingAdjustment({ primaryCoatingPerUnit, ppq, hasCoatingRow }) {
  if (!hasCoatingRow) return 0;
  const c = num(primaryCoatingPerUnit);
  if (c <= 0) return 0;
  return c * Math.max(1, parseInt(ppq, 10) || 1);
}

// ---------------------------------------------------------------------------
// Card model — ONE shape for the server-fed and local paths.
// ---------------------------------------------------------------------------
function manufacturedLineFromComponent(comp, qty) {
  const v = componentCostView(comp);
  return {
    part_id: comp.part_id,
    product_id: comp.product_id,
    role: comp.is_primary ? 'primary' : 'component',
    qty_per_assembly: qty,
    unit_cost_stored: v.stored,
    unit_cost_live: v.live,
    // The local live figure is the ROLLUP over stored components (server
    // live_total or the UI mirror) — it still contains the part's coating,
    // so the F07 adjustment applies to it. Only the quote engine strips it.
    live_basis: v.live === null ? null : 'rollup',
    live_excludes_coating: false,
    excluded_steps: [],
    coating_priced_on_ops_line: false,
    unit_price_calc: null,
    unit_book_price: v.bookPrice,
    book_price_source: v.bookPriceSource,
    book_price_quote_id: v.bookPriceQuoteId,
    book_price_set_at: v.bookPriceSetAt,
    // null = cannot be judged (the row carries no gas/consumable components,
    // so no live rollup exists) — distinct from a verified "not stale".
    stale: v.live === null ? null : v.stale,
    stale_basis: v.live === null ? null : 'rollup',
    coating_per_unit: v.coating,
    material_price_method: v.materialMethod,
    cost_updated_at: v.costUpdatedAt,
    cost_breakdown: null,
    error: null,
  };
}

/**
 * Local (fallback) model — stored rollups + vendor costs + the ops figure the
 * page already has. `components` must already carry the EFFECTIVE primary qty
 * (the ppq input, F21). `ops` = { operations, cost, price, isEstimate }.
 */
export function buildLocalCardModel({ components, consumables, ppq, ops, assembly, materialMarkup }) {
  const mm = num(materialMarkup, CONSUMABLE_SUGGESTED_MARKUP);
  const manufacturedLines = (components || []).map((c) => manufacturedLineFromComponent(c, num(c.quantity_per_assembly)));
  const purchasedLines = (consumables || []).map((c) => {
    const v = consumableView(c, mm);
    return {
      consumable_id: c.consumable_id,
      product_id: c.product_id,
      qty_per_assembly: num(c.quantity_per_assembly),
      unit_cost: v.cost,
      cost_source: v.costSource,
      book_price: v.hasBook ? v.book : null,
      below_cost: v.belowCost,
      suggested_price: v.suggested,
    };
  });
  const operations = ops || {};
  const opRows = operations.operations || [];
  const primary = manufacturedLines.find((l) => l.role === 'primary');
  const hasCoatingRow = operations.hasCoatingRow !== undefined ? !!operations.hasCoatingRow : opsHasCoatingRow(opRows);
  const a = assembly || {};
  return {
    source: 'local',
    assembly_id: a.assembly_id ?? null,
    ppq: Math.max(1, parseInt(ppq, 10) || 1),
    rates_used: { ...ASSEMBLY_DEFAULT_QUOTE_RATES },
    computed_at: null,
    manufactured: { lines: manufacturedLines },
    purchased: { lines: purchasedLines },
    operations: {
      rows: opRows,
      cost: (operations.cost === null || operations.cost === undefined) ? null : num(operations.cost, null),
      price: (operations.price === null || operations.price === undefined) ? null : num(operations.price, null),
      has_coating_row: hasCoatingRow,
      coating_cost: opsCoatingCost(opRows),
      is_estimate: !!operations.isEstimate,
      rate_set: operations.isEstimate ? 'standard wage table (unsaved estimate)' : DEFAULT_RATE_SET_NAME,
    },
    coating_adjustment: {
      primary_coating_double_count: coatingAdjustment({
        primaryCoatingPerUnit: primary ? primary.coating_per_unit : 0,
        ppq,
        hasCoatingRow,
      }),
    },
    system_price: {
      value: (a.assembly_price === null || a.assembly_price === undefined) ? null : num(a.assembly_price, null),
      source: a.assembly_price_source || null,
      quote_id: a.assembly_price_quote_id ?? null,
      set_at: a.assembly_price_set_at || null,
    },
    last_quote: null,
    server_per_set: null,
  };
}

/** Normalise GET /assemblies/{id}/pricing (spec §3 B4) into the card shape.
 * Missing keys become nulls — never NaN. */
export function normalizePricingResponse(p, assembly) {
  const d = p || {};
  const man = d.manufactured || {};
  const pur = d.purchased || {};
  const ops = d.operations || {};
  const a = assembly || {};
  const sys = d.system_price || {};
  const opRows = ops.rows || [];
  const hasCoatingRow = ops.has_coating_row !== undefined ? !!ops.has_coating_row : opsHasCoatingRow(opRows);
  const manufacturedLines = (man.lines || []).map((l) => {
    const role = l.role || 'component';
    const live = (l.unit_cost_live === null || l.unit_cost_live === undefined) ? null : num(l.unit_cost_live, null);
    const excludedSteps = Array.isArray(l.excluded_steps) ? l.excluded_steps : [];
    // The server's unit_cost_live is the QUOTE ENGINE's total_cost. For the
    // primary of a row-bearing assembly the engine already strips the steps
    // (and the coating) the operations line owns, so that live figure must
    // NOT have the F07 adjustment taken off it again — the server says so
    // with `coating_priced_on_ops_line`; an older server that lacks the flag
    // but returns an engine cost_breakdown gets the same treatment.
    const coatingOnOps = l.coating_priced_on_ops_line === true
      || (l.coating_priced_on_ops_line === undefined && role === 'primary' && hasCoatingRow && !!l.cost_breakdown);
    const liveExcludesCoating = live !== null && coatingOnOps;
    const partiallyPriced = coatingOnOps || excludedSteps.length > 0;
    return {
      part_id: l.part_id,
      product_id: l.product_id,
      description: l.description || null,
      role,
      qty_per_assembly: num(l.qty_per_assembly),
      unit_cost_stored: (l.unit_cost_stored === null || l.unit_cost_stored === undefined) ? null : num(l.unit_cost_stored, null),
      unit_cost_live: live,
      live_basis: live === null ? null : (l.cost_breakdown || coatingOnOps ? 'engine' : 'rollup'),
      live_excludes_coating: liveExcludesCoating,
      excluded_steps: excludedSteps,
      coating_priced_on_ops_line: coatingOnOps,
      unit_price_calc: (l.unit_price_calc === null || l.unit_price_calc === undefined) ? null : num(l.unit_price_calc, null),
      unit_book_price: (l.book_price === null || l.book_price === undefined)
        ? ((l.price === null || l.price === undefined) ? null : num(l.price, null)) : num(l.book_price, null),
      book_price_source: l.book_price_source || null,
      book_price_quote_id: l.book_price_quote_id ?? null,
      book_price_set_at: l.book_price_set_at || null,
      // The server's `stale` compares engine-live with the stored rollup. On a
      // line the engine priced only PARTLY (ops-owned steps / coating stripped)
      // that gap is expected, not staleness — leave it unknown (null) so the
      // page's own rollup check (enrichServerLinesFromComponents) can decide.
      stale: partiallyPriced ? null : (typeof l.stale === 'boolean' ? l.stale : null),
      stale_basis: partiallyPriced ? null : (typeof l.stale === 'boolean' ? 'engine' : null),
      coating_per_unit: (l.coating_cost_per_unit === null || l.coating_cost_per_unit === undefined) ? null : num(l.coating_cost_per_unit),
      material_price_method: l.material_price_method || null,
      cost_updated_at: l.cost_updated_at || null,
      cost_breakdown: l.cost_breakdown || null,
      error: l.error || null,
    };
  });
  const purchasedLines = (pur.lines || []).map((l) => {
    const v = consumableView({
      cost: l.unit_cost, price: l.book_price, below_cost: l.below_cost,
      suggested_price: l.suggested_price, cost_source: l.cost_source, cost_vendor_id: l.cost_vendor_id,
    }, num((d.rates_used || {}).material_markup, CONSUMABLE_SUGGESTED_MARKUP));
    return {
      consumable_id: l.consumable_id,
      product_id: l.product_id,
      qty_per_assembly: num(l.qty_per_assembly),
      unit_cost: v.cost,
      cost_source: v.costSource,
      book_price: v.hasBook ? v.book : null,
      below_cost: v.belowCost,
      suggested_price: v.suggested,
    };
  });
  const primary = manufacturedLines.find((l) => l.role === 'primary');
  const ca = d.coating_adjustment || {};
  const perSet = d.per_set || {};
  return {
    source: 'server',
    assembly_id: d.assembly_id ?? a.assembly_id ?? null,
    ppq: Math.max(1, parseInt(d.ppq, 10) || parseInt(a.primary_part_quantity, 10) || 1),
    rates_used: { ...ASSEMBLY_DEFAULT_QUOTE_RATES, ...(d.rates_used || {}) },
    computed_at: d.computed_at || null,
    sets_basis: d.sets_basis || null,
    manufactured: { lines: manufacturedLines },
    purchased: { lines: purchasedLines },
    operations: {
      rows: opRows,
      cost: (ops.cost === null || ops.cost === undefined) ? null : num(ops.cost, null),
      price: (ops.price === null || ops.price === undefined) ? null : num(ops.price, null),
      has_coating_row: hasCoatingRow,
      coating_cost: (ops.coating_cost === null || ops.coating_cost === undefined) ? opsCoatingCost(opRows) : num(ops.coating_cost),
      is_estimate: false,
      rate_set: DEFAULT_RATE_SET_NAME,
      // The server prices the ops leg under a SAVEPOINT; when that call fails
      // rows are empty and cost/price are 0 with `error` set — the card must
      // say so rather than print a $0.00 operations line as fact.
      error: ops.error || null,
      row_count: (ops.row_count === null || ops.row_count === undefined) ? opRows.length : num(ops.row_count, opRows.length),
      ops_owned_steps: Array.isArray(ops.ops_owned_steps) ? ops.ops_owned_steps : [],
    },
    coating_adjustment: {
      primary_coating_double_count: (ca.primary_coating_double_count === null || ca.primary_coating_double_count === undefined)
        ? coatingAdjustment({ primaryCoatingPerUnit: primary ? primary.coating_per_unit : 0, ppq: d.ppq || a.primary_part_quantity, hasCoatingRow })
        : num(ca.primary_coating_double_count),
    },
    system_price: {
      value: (sys.value === null || sys.value === undefined)
        ? ((a.assembly_price === null || a.assembly_price === undefined) ? null : num(a.assembly_price, null))
        : num(sys.value, null),
      source: sys.source || a.assembly_price_source || null,
      quote_id: sys.quote_id ?? a.assembly_price_quote_id ?? null,
      set_at: sys.set_at || a.assembly_price_set_at || null,
    },
    last_quote: d.last_quote || null,
    // The server's own formula strings (per_set.*_formula), shown as-is so the
    // operator can see the producer's definition next to the consumer's sums.
    server_formulas: {
      cost: perSet.cost_formula || null,
      cost_stored: perSet.cost_stored_formula || null,
      price: perSet.price_formula || null,
      book: perSet.book_price_formula || null,
    },
    // The server's own per-set figures, kept ONLY for the producer/consumer
    // drift check (perSetDrift). Its margin_pct is a PERCENT (69.1) where this
    // module's margins are fractions, so it is deliberately not carried.
    server_per_set: {
      cost_live: (perSet.cost_live === null || perSet.cost_live === undefined) ? null : num(perSet.cost_live, null),
      cost_stored: (perSet.cost_stored === null || perSet.cost_stored === undefined) ? null : num(perSet.cost_stored, null),
      price_calc: (perSet.price_calc === null || perSet.price_calc === undefined) ? null : num(perSet.price_calc, null),
      book_price: (perSet.book_price === null || perSet.book_price === undefined) ? null : num(perSet.book_price, null),
    },
  };
}

/**
 * While the operations grid has unsaved edits, the saved ops figure is wrong
 * by construction; swap in the local estimate and mark it. Ops PRICE is not
 * knowable until the rows are saved (the calculator prices SAVED rows), so it
 * becomes null and the quotable price goes n/a — honest, not stale.
 */
export function applyOpsEstimate(model, estimate) {
  if (!model || !estimate) return model;
  let lines = model.manufactured.lines || [];
  let primary = lines.find((l) => l.role === 'primary');
  // The engine priced the primary WITHOUT its coating because the SAVED rows
  // had a Coating row. If the unsaved grid no longer has one, that coating is
  // nobody's now — put it back on the primary's live figure so the estimate
  // does not quietly drop $15 of real cost. (The mirror case — a Coating row
  // added to the grid while the engine figure still contains the coating — is
  // handled by the ordinary F07 subtraction in perSetTotals.)
  if (primary && !estimate.hasCoatingRow && primary.live_excludes_coating
      && primary.unit_cost_live !== null && num(primary.coating_per_unit) > 0) {
    primary = {
      ...primary,
      unit_cost_live: primary.unit_cost_live + num(primary.coating_per_unit),
      live_excludes_coating: false,
      coating_priced_on_ops_line: false,
    };
    lines = lines.map((l) => (l.role === 'primary' ? primary : l));
  }
  return {
    ...model,
    manufactured: { ...model.manufactured, lines },
    operations: {
      rows: estimate.rows,
      cost: estimate.total,
      price: null,
      has_coating_row: estimate.hasCoatingRow,
      coating_cost: estimate.coatingCost,
      is_estimate: true,
      rate_set: 'standard wage table (unsaved estimate)',
    },
    coating_adjustment: {
      primary_coating_double_count: coatingAdjustment({
        primaryCoatingPerUnit: primary ? primary.coating_per_unit : 0,
        ppq: model.ppq,
        hasCoatingRow: estimate.hasCoatingRow,
      }),
    },
    server_per_set: null,
  };
}

/**
 * F21: the primary row follows the ppq INPUT, not the saved value. Re-scales
 * the primary line's qty and the coating adjustment; the server's per_set
 * figures no longer apply at a different ppq, so they are dropped.
 */
export function applyPpq(model, ppq) {
  if (!model) return model;
  const p = Math.max(1, parseInt(ppq, 10) || 1);
  if (p === model.ppq) return model;
  const lines = (model.manufactured.lines || []).map((l) => (l.role === 'primary' ? { ...l, qty_per_assembly: p } : l));
  const primary = lines.find((l) => l.role === 'primary');
  const oldAdj = num((model.coating_adjustment || {}).primary_coating_double_count);
  // Per-unit coating from the line when known; else back it out of the
  // server's adjustment at the saved ppq (the spec's server line shape does
  // not carry coating_cost_per_unit).
  const perUnit = (primary && primary.coating_per_unit !== null && primary.coating_per_unit !== undefined)
    ? primary.coating_per_unit
    : (model.ppq > 0 ? oldAdj / model.ppq : 0);
  return {
    ...model,
    ppq: p,
    manufactured: { ...model.manufactured, lines },
    coating_adjustment: {
      primary_coating_double_count: coatingAdjustment({
        primaryCoatingPerUnit: perUnit,
        ppq: p,
        hasCoatingRow: !!(model.operations && model.operations.has_coating_row) || oldAdj > 0,
      }),
    },
    server_per_set: null,
  };
}

/**
 * The server's manufactured line shape (spec B4) carries no book price and no
 * coating_cost_per_unit; the page already holds both on the component rows it
 * loaded. Fill them in by part_id so the Book price row and the ppq-scaled
 * coating adjustment stay reproducible from the tables on screen. Lines that
 * already carry a value are left alone.
 */
export function enrichServerLinesFromComponents(model, components) {
  if (!model || !components || components.length === 0) return model;
  const byPart = new Map();
  components.forEach((c) => { if (c && c.part_id !== undefined && c.part_id !== null) byPart.set(Number(c.part_id), c); });
  let changed = false;
  const lines = (model.manufactured.lines || []).map((l) => {
    const c = byPart.get(Number(l.part_id));
    if (!c) return l;
    const v = componentCostView(c);
    const next = { ...l };
    let lineChanged = false;
    if ((l.unit_book_price === null || l.unit_book_price === undefined) && v.bookPrice !== null) { next.unit_book_price = v.bookPrice; lineChanged = true; }
    if ((l.coating_per_unit === null || l.coating_per_unit === undefined) && v.coating !== null) { next.coating_per_unit = v.coating; lineChanged = true; }
    if ((l.unit_cost_stored === null || l.unit_cost_stored === undefined) && v.stored !== null) { next.unit_cost_stored = v.stored; lineChanged = true; }
    if (!l.material_price_method && v.materialMethod) { next.material_price_method = v.materialMethod; lineChanged = true; }
    if (!l.cost_updated_at && v.costUpdatedAt) { next.cost_updated_at = v.costUpdatedAt; lineChanged = true; }
    // A line whose engine-vs-stored gap is expected (ops-owned steps / coating
    // stripped) has stale = null; the components table's ROLLUP check
    // (stored total vs its own components, F06) is the right judge there.
    if ((l.stale === null || l.stale === undefined) && v.live !== null) {
      next.stale = v.stale; next.stale_basis = 'rollup'; next.unit_cost_rollup = v.live; lineChanged = true;
    }
    if (lineChanged) changed = true;
    return lineChanged ? next : l;
  });
  return changed ? { ...model, manufactured: { ...model.manufactured, lines } } : model;
}

/** Sum helper: null when ANY term is null (so an incomplete price never prints
 * as a smaller real number); `missing` counts the null terms. */
function sumOrNull(values) {
  let total = 0; let missing = 0;
  values.forEach((v) => { if (v === null || v === undefined || Number.isNaN(Number(v))) missing += 1; else total += Number(v); });
  return { total: missing > 0 ? null : total, missing };
}

/**
 * The per-set arithmetic the card prints. Every output is reproducible from the
 * line tables:
 *   manufactured.cost_live   = Σ unit_cost_live × qty      (primary qty = ppq)
 *   purchased.cost           = Σ unit_cost × qty
 *   operations.cost          = ops cost (saved calc or unsaved estimate)
 *   coating_adjustment       = ppq × primary coating_cost_per_unit when the
 *                              assembly has a Coating row (F07) — always taken
 *                              off the STORED basis (the stored rollup contains
 *                              the coating); taken off the LIVE basis only when
 *                              the primary's live figure still contains it (a
 *                              rollup, or a stored fallback) — the quote engine's
 *                              figure (`live_excludes_coating`) already left it out
 *   shop cost (live)         = manufactured.cost_live + purchased.cost + operations.cost − coating_adjustment_live
 *   shop cost (stored)       = manufactured.cost_stored + purchased.cost + operations.cost − coating_adjustment
 *   quotable price           = Σ unit_price_calc × qty + Σ suggested × qty + operations.price
 *   book price               = Σ book × qty (parts + consumables; no ops)
 */
export function perSetTotals(model) {
  const m = model || { manufactured: { lines: [] }, purchased: { lines: [] }, operations: {}, coating_adjustment: {}, system_price: {} };
  const manLines = (m.manufactured && m.manufactured.lines) || [];
  const purLines = (m.purchased && m.purchased.lines) || [];
  const ops = m.operations || {};

  const manStored = sumOrNull(manLines.map((l) => (l.unit_cost_stored === null ? null : l.unit_cost_stored * l.qty_per_assembly)));
  // Live falls back to stored per line so one row without provenance does not
  // blank the whole figure; the fallback is counted so the card can say so.
  let manLiveFallbacks = 0;
  const manLive = sumOrNull(manLines.map((l) => {
    if (l.unit_cost_live !== null && l.unit_cost_live !== undefined) return l.unit_cost_live * l.qty_per_assembly;
    if (l.unit_cost_stored !== null && l.unit_cost_stored !== undefined) { manLiveFallbacks += 1; return l.unit_cost_stored * l.qty_per_assembly; }
    return null;
  }));
  const manPrice = sumOrNull(manLines.map((l) => (l.unit_price_calc === null || l.unit_price_calc === undefined ? null : l.unit_price_calc * l.qty_per_assembly)));
  const manBook = manLines.reduce((s, l) => s + (l.unit_book_price ? l.unit_book_price * l.qty_per_assembly : 0), 0);

  const purCost = sumOrNull(purLines.map((l) => (l.unit_cost === null ? null : l.unit_cost * l.qty_per_assembly)));
  const purSuggested = sumOrNull(purLines.map((l) => (l.suggested_price === null ? null : l.suggested_price * l.qty_per_assembly)));
  const purBook = purLines.reduce((s, l) => s + (l.book_price ? l.book_price * l.qty_per_assembly : 0), 0);
  const purBelowCost = purLines.filter((l) => l.below_cost).length;

  const opsCost = (ops.cost === null || ops.cost === undefined) ? 0 : num(ops.cost);
  const opsPrice = (ops.price === null || ops.price === undefined) ? null : num(ops.price);
  const coatingAdj = num((m.coating_adjustment || {}).primary_coating_double_count);
  // F07, live basis: the primary's live figure still carries its coating when
  // it is a rollup over stored components or the stored value standing in for
  // a failed engine call; the quote engine's own figure does not.
  const primary = manLines.find((l) => l.role === 'primary');
  const liveCoatingExcludedByEngine = !!(primary && primary.unit_cost_live !== null && primary.unit_cost_live !== undefined
    && primary.live_excludes_coating);
  const coatingAdjLive = liveCoatingExcludedByEngine ? 0 : coatingAdj;

  const costLive = (manLive.total === null || purCost.total === null) ? null : manLive.total + purCost.total + opsCost - coatingAdjLive;
  const costStored = (manStored.total === null || purCost.total === null) ? null : manStored.total + purCost.total + opsCost - coatingAdj;
  const priceCalc = (manPrice.total === null || purSuggested.total === null || opsPrice === null) ? null : manPrice.total + purSuggested.total + opsPrice;
  // Book price: line sum when any line carries one; else the server's own
  // per_set.book_price (its line shape has no book price — see
  // enrichServerLinesFromComponents), else nothing.
  let bookPrice = manBook + purBook;
  let bookSource = 'lines';
  const anyLineBook = manLines.some((l) => l.unit_book_price) || purLines.some((l) => l.book_price);
  if (!anyLineBook && m.server_per_set && m.server_per_set.book_price !== null && m.server_per_set.book_price !== undefined) {
    bookPrice = num(m.server_per_set.book_price);
    bookSource = 'server';
  }
  const sysValue = (m.system_price && m.system_price.value) ? num(m.system_price.value) : null;

  return {
    manufactured: {
      cost_stored: manStored.total,
      cost_live: manLive.total,
      live_fallbacks: manLiveFallbacks,
      price_calc: manPrice.total,
      price_missing_lines: manPrice.missing,
      book_price: manBook,
      lines: manLines.length,
      stale_lines: manLines.filter((l) => l.stale === true).length,
      unjudged_lines: manLines.filter((l) => l.stale === null || l.stale === undefined).length,
      error_lines: manLines.filter((l) => l.error).length,
      primary_excluded_steps: primary ? (primary.excluded_steps || []) : [],
    },
    purchased: {
      cost: purCost.total,
      suggested_price: purSuggested.total,
      book_price: purBook,
      lines: purLines.length,
      below_cost_lines: purBelowCost,
    },
    operations: {
      cost: opsCost,
      price: opsPrice,
      coating_cost: num(ops.coating_cost),
      is_estimate: !!ops.is_estimate,
      has_coating_row: !!ops.has_coating_row,
      error: ops.error || null,
    },
    coating_adjustment: coatingAdj,
    coating_adjustment_live: coatingAdjLive,
    live_coating_excluded_by_engine: liveCoatingExcludedByEngine,
    cost_live: costLive,
    cost_stored: costStored,
    price_calc: priceCalc,
    margin_pct: marginPct(costLive, priceCalc),
    book_price: bookPrice > 0 ? bookPrice : null,
    book_price_source: bookSource,
    system_price: sysValue,
    system_margin_pct: (sysValue !== null && costLive !== null) ? marginPct(costLive, sysValue) : null,
  };
}

/** Compare the UI bucket sum with the server's per_set figures (producer vs
 * consumer). Returns a list of human drift strings; empty when they agree. */
export function perSetDrift(model, totals, tol = 0.02) {
  const sp = model && model.server_per_set;
  if (!sp || !totals) return [];
  const out = [];
  const check = (label, mine, theirs) => {
    if (mine === null || theirs === null || mine === undefined || theirs === undefined) return;
    if (Math.abs(num(mine) - num(theirs)) > tol) {
      out.push(`${label}: bucket sum $${num(mine).toFixed(2)} vs server per_set $${num(theirs).toFixed(2)}`);
    }
  };
  check('shop cost (live)', totals.cost_live, sp.cost_live);
  check('shop cost (stored)', totals.cost_stored, sp.cost_stored);
  check('quotable price', totals.price_calc, sp.price_calc);
  if (totals.book_price_source === 'lines') check('book price', totals.book_price, sp.book_price === 0 ? null : sp.book_price);
  return out;
}

// ---------------------------------------------------------------------------
// F51 — assembly_price save semantics
// ---------------------------------------------------------------------------
/** Parse the "System Price" box: '' → null, else a finite number (else null). */
export function parseAssemblyPriceInput(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Decide whether to send assembly_price and whether to confirm first.
 *   loaded — the value the form was seeded with (raw API value; null/0 = unset)
 *   input  — the box contents
 * Returns { send, value, needsConfirm, message }.
 */
export function assemblyPriceSaveDecision(loaded, input) {
  // A stored 0.0 seeds an EMPTY box (every consumer treats 0 and NULL as
  // "not set"), so it is unset here too — otherwise every unrelated save
  // would rewrite 0.0 → NULL.
  let loadedVal = (loaded === null || loaded === undefined || loaded === '') ? null : num(loaded, null);
  if (loadedVal === 0) loadedVal = null;
  const value = parseAssemblyPriceInput(input);
  const same = (loadedVal === null && value === null)
    || (loadedVal !== null && value !== null && approxEq(loadedVal, value));
  if (same) return { send: false, value: loadedVal, needsConfirm: false, message: null };
  const clearing = value === null && loadedVal !== null && loadedVal > 0;
  return {
    send: true,
    value,
    needsConfirm: clearing,
    message: clearing
      ? `Clear the System Price? The stored value $${loadedVal.toFixed(2)} will be set to "not set" — the Quote page's Assembly Pricing card will show "Not set" and stop seeding overrides from it.`
      : null,
  };
}

/** Human label for the assembly_price / book-price provenance stamp. */
export function priceProvenanceLabel({ source, quoteId, setAt }) {
  const when = setAt ? new Date(setAt) : null;
  const whenStr = when && !Number.isNaN(when.getTime()) ? when.toLocaleDateString() : null;
  if (source === 'quote_approval') return `from Quote #${quoteId ?? '?'} approved${whenStr ? ` ${whenStr}` : ''}`;
  if (source === 'manual') return `manual${whenStr ? ` (${whenStr})` : ''}`;
  if (source) return `${source}${whenStr ? ` (${whenStr})` : ''}`;
  return 'provenance not recorded';
}
